import { RAID_SIZE } from "@/shared/lib/raid";
import type { RaidRole } from "@/shared/lib/roles";
import { WOW_CLASS_LABELS, type WowClass } from "@/shared/lib/wowClass";

export type RuleCount =
  | { kind: "total" }
  | { kind: "role"; role: RaidRole }
  | { kind: "class"; wowClass: WowClass };

export type SetupRule = {
  id: string;
  /** Builds the failure text from how many are missing (e.g. "Saknar 1 tank", "Warrior"). */
  label: (missing: number) => string;
  count: RuleCount;
  /** count < errorBelow → red error */
  errorBelow?: number;
  /** otherwise count < warnBelow → orange warning */
  warnBelow?: number;
};

// Failing count rules show the shortfall ("5 spelare", "1 tank") — the
// panel's "Saknar" heading provides the verb.
const missingNoun = (singular: string, plural: string) => (n: number) =>
  `${n} ${n === 1 ? singular : plural}`;

// A missing class reads as just the class name — the severity icon says the rest.
const classRule = (
  c: WowClass,
  severity: Pick<SetupRule, "errorBelow" | "warnBelow">,
): SetupRule => ({
  id: `class-${c}`,
  label: () => WOW_CLASS_LABELS[c],
  count: { kind: "class", wowClass: c },
  ...severity,
});

// Each of these classes brings a unique raid buff — missing one is an error.
const ERROR_CLASSES: WowClass[] = [
  "demon_hunter",
  "mage",
  "warrior",
  "druid",
  "hunter",
  "monk",
  "paladin",
  "priest",
  "shaman",
  "warlock",
];
// Nice-to-have utility — missing one is only a warning.
const WARN_CLASSES: WowClass[] = ["rogue", "death_knight", "evoker"];

export const SETUP_RULES: SetupRule[] = [
  {
    id: "total",
    label: missingNoun("spelare", "spelare"),
    count: { kind: "total" },
    errorBelow: RAID_SIZE,
  },
  {
    id: "tanks",
    label: missingNoun("tank", "tanks"),
    count: { kind: "role", role: "tank" },
    errorBelow: 2,
  },
  {
    id: "healers",
    label: missingNoun("healer", "healers"),
    count: { kind: "role", role: "healer" },
    errorBelow: 3,
    warnBelow: 4,
  },
  ...ERROR_CLASSES.map((c) => classRule(c, { errorBelow: 1 })),
  ...WARN_CLASSES.map((c) => classRule(c, { warnBelow: 1 })),
];

export type RulePlayer = {
  wowClass: string | null;
  raidRole: string | null;
  benched: boolean;
};

// `label` is the complete display string, so the panel renders every failure uniformly.
export type RuleFailure = {
  id: string;
  label: string;
  severity: "error" | "warning";
};

function countFor(players: RulePlayer[], count: RuleCount): number {
  switch (count.kind) {
    case "total":
      return players.length;
    case "role":
      return players.filter((p) => p.raidRole === count.role).length;
    case "class":
      return players.filter((p) => p.wowClass === count.wowClass).length;
  }
}

// Returns only failing rules (the UI shows "Setup OK" when this is empty), errors first.
// Absent players must not be passed in; benched are filtered here.
export function evaluateRules(
  players: RulePlayer[],
  rules: SetupRule[],
): RuleFailure[] {
  const active = players.filter((p) => !p.benched);
  const failures: RuleFailure[] = [];

  for (const rule of rules) {
    const count = countFor(active, rule.count);
    // Shortfall against the desired minimum (the higher threshold when tiered).
    const missing = (rule.warnBelow ?? rule.errorBelow ?? 0) - count;
    if (rule.errorBelow !== undefined && count < rule.errorBelow) {
      failures.push({
        id: rule.id,
        label: rule.label(missing),
        severity: "error",
      });
    } else if (rule.warnBelow !== undefined && count < rule.warnBelow) {
      failures.push({
        id: rule.id,
        label: rule.label(missing),
        severity: "warning",
      });
    }
  }

  return failures.sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1,
  );
}
